// AI 深度五档:纯逻辑层(档位定义 / 设置接口 / 模块级快照)。
// 档位只有中性名称(快速/均衡/深入/专家/极致)——前端永远不出现任何模型名/厂商名,这是文案红线。
// 倍率与文案以后端 GET /api/settings 返回的 depth_tiers 为准;下面的 FALLBACK_TIERS
// 仅用于设置尚未拉到(未登录 / 请求在途 / 后端未升级)时的展示兜底。
import { http } from "./http";

export interface DepthTier {
  depth: number; // 1..5
  label: string;
  desc: string;
  multiplier: number; // 积分倍率:LLM 功能实际扣费 = 基础价 × multiplier
}

export const MIN_DEPTH = 1;
export const MAX_DEPTH = 5;

export const FALLBACK_TIERS: DepthTier[] = [
  { depth: 1, label: "快速", desc: "日常自查,秒回", multiplier: 1 },
  { depth: 2, label: "均衡", desc: "更完整的推理与核对", multiplier: 2 },
  { depth: 3, label: "深入", desc: "多角度审视,结论更稳", multiplier: 3 },
  { depth: 4, label: "专家", desc: "逐项深挖,适合投稿前把关", multiplier: 4 },
  { depth: 5, label: "极致", desc: "最深入的思考与最审慎的结论", multiplier: 5 },
];

export function clampDepth(d: number): number {
  if (!Number.isFinite(d)) return MIN_DEPTH;
  return Math.min(MAX_DEPTH, Math.max(MIN_DEPTH, Math.round(d)));
}

/** 后端 depth_tiers 清洗:字段缺失/类型不对时逐项兜底,数量不足 5 档直接退回 FALLBACK。 */
export function normalizeTiers(raw: unknown): DepthTier[] {
  if (!Array.isArray(raw) || raw.length < MAX_DEPTH) return FALLBACK_TIERS;
  const out: DepthTier[] = [];
  for (let d = MIN_DEPTH; d <= MAX_DEPTH; d++) {
    const hit = raw.find((t) => t && typeof t === "object" && clampDepth(Number((t as { depth?: unknown }).depth)) === d) as
      | Partial<DepthTier>
      | undefined;
    const fb = FALLBACK_TIERS[d - 1];
    out.push({
      depth: d,
      label: typeof hit?.label === "string" && hit.label ? hit.label : fb.label,
      desc: typeof hit?.desc === "string" && hit.desc ? hit.desc : fb.desc,
      multiplier:
        typeof hit?.multiplier === "number" && Number.isFinite(hit.multiplier) && hit.multiplier > 0
          ? hit.multiplier
          : fb.multiplier,
    });
  }
  return out;
}

export function tierOf(depth: number, tiers: DepthTier[]): DepthTier {
  return tiers.find((t) => t.depth === clampDepth(depth)) ?? tiers[0] ?? FALLBACK_TIERS[0];
}

/** LLM 功能按档位缩放后的价格(向上取整,与后端"基础价×倍率"对齐;倍率为整数时无差异)。 */
export function scaledCost(base: number, depth: number, tiers: DepthTier[]): number {
  return Math.ceil(base * tierOf(depth, tiers).multiplier);
}

// ---------- 模块级快照:供非 React 层(lib/*Api.ts 的请求体)读取当前生效档位 ----------
// AiDepthContext 每次状态变化都会同步到这里;Provider 尚未挂载/未登录时保持默认 1 档。
let effectiveDepthSnapshot = MIN_DEPTH;

export function getEffectiveDepth(): number {
  return effectiveDepthSnapshot;
}

/** 仅供 AiDepthContext 同步调用,业务代码不要直接改。 */
export function syncEffectiveDepthSnapshot(depth: number): void {
  effectiveDepthSnapshot = clampDepth(depth);
}

// ---------- 设置接口(GET/PATCH /api/settings,契约见后端 resguard) ----------
export interface SettingsResponse {
  ok: boolean;
  settings?: { ai_depth?: number };
  depth_tiers?: unknown;
}

export const settingsApi = {
  get: () => http.get<SettingsResponse>("/api/settings"),
  patchAiDepth: (aiDepth: number) => http.patch<SettingsResponse>("/api/settings", { ai_depth: clampDepth(aiDepth) }),
};
